<template>
    <loading  v-if="isLoading" :message="$t('COPIED_ENVIRONMENT_LOADING_MESSAGE')"/>
    <v-alert v-else-if="error && !retryableError" role="alert" type="error" icon="error" dense :value="error" class="mb-4">{{ error }}</v-alert>
    <div v-else class="elevation-0">
        <h1>{{$t('COPIED_ENVIRONMENT_SHARING_CONFIRMATION')}}</h1>

        <template v-if="catalogFamilyAccess.id">
            <v-alert v-if="error" role="alert" type="error" icon="error" dense :value="error" class="mb-4">{{ error }}</v-alert>
            <announcement/>
            <p  v-if="getDisplayName" class="mt-3 mb-3" v-html="$t('COPIED_ENVIRONMENT_WELCOME_MESSAGE', {name: getDisplayName})"></p>
            <p class="mt-3 mb-3" v-html="$t('COPIED_ENVIRONMENT_SHARE_INFORMATION', {sharedByName: catalogFamilyAccess.sharedByName, familyName: catalogFamilyAccess.catalogFamilyName, familyVersion: catalogFamilyAccess.catalogFamilyVersion })"></p>
            <v-spacer></v-spacer>
            <v-spacer></v-spacer>
        </template>
        <template v-if="showOrganizationSelect">
            <AccessibleForm :submit-function="onOrganizationSelectConfirmed">
                <p
                    class="mt-3 mb-3 subheading"
                    v-html="$t('COPIED_ENVIRONMENT_SHARE_ORGANIZATION_SELECT_INSTRUCTIONS')"
                />
                <accessible-combobox
                    v-if="selectableOrganizations.length > 1"
                    :items="selectableOrganizations"
                    item-text="name"
                    item-value="id"
                    v-model="selectedOrganizationId"
                    :disabled="isLoading"
                    required
                    v-validate="`required`"
                    data-vv-name="copiedEnvironmentShareOrganizationSelect"
                />
                <v-layout row wrap>
                    <v-btn
                        :disabled="isLoading"
                        color="cancel"
                        @click="onOrganizationSelectCancelled"
                        class="elevation-0"
                        :aria-label="$t('COPIED_ENVIRONMENT_ORGANIZATION_SELECT_CANCEL_BUTTON')"
                        >{{$t('COPIED_ENVIRONMENT_ORGANIZATION_SELECT_CANCEL_BUTTON')}}</v-btn>
                    <v-btn
                        type="submit"
                        :disabled="isLoading"
                        :loading="isLoading"
                        :dark="!isLoading"
                        color="confirm"
                        class="elevation-0 ml-2"
                        :aria-label="$t('COPIED_ENVIRONMENT_ORGANIZATION_SELECT_CONFIRM_BUTTON')"
                        >{{$t('COPIED_ENVIRONMENT_ORGANIZATION_SELECT_CONFIRM_BUTTON')}}</v-btn>
                </v-layout>
            </AccessibleForm>
        </template>
        <template v-else-if="catalogFamilyAccess.id">
            <p class="mt-3 mb-3" v-html="$t('COPIED_ENVIRONMENT_ACCEPT_INSTRUCTIONS')"></p>
            <login-buttons @confirm="onLoginSuccess" mode="invitations" :providers="['google', 'azure']"/>
        </template>
    </div>
</template>


<script lang="ts">
import Vue from "vue";
import Loading from "@utils/Loading.vue";
import LoginButtons from "../login/LoginButtons.vue";
import Announcement from "@utils/Announcement.vue";
import Component from "vue-class-component";
import Config from '@/config';
import { useAuthorizationStore } from "@/stores/authorizationStore";
import { CatalogFamilyAccessType, ICatalogFamilyAccess } from "@cyber-range/cyber-range-api-catalog-client";
import { IOrganization } from "@cyber-range/cyber-range-api-organization-client";
import AccessibleCombobox from '@components/utils/AccessibleCombobox.vue';
import AccessibleForm from '@components/utils/AccessibleForm.vue'
import { ForbiddenApiClientError } from "@cyber-range/cyber-range-api-client";
import { useErrorStore } from "@/stores/errorStore";
import { useApiClientStore } from "@/stores/apiClientStore";
import { useAuthenticationStore } from "@/stores/authenticationStore";
import { useEntitlementStore } from "@/stores/entitlementStore";
import { storeToRefs } from "pinia";

@Component({
    components: { Loading, LoginButtons, Announcement, AccessibleCombobox, AccessibleForm  }
})
export default class CopiedEnvironmentShare extends Vue {
    getSsoToken = () => useAuthenticationStore().getSsoToken();
    fetchClaims = () => useEntitlementStore().fetchClaims();

    get catalogFamilyAccessApiClient() { return useApiClientStore().catalogFamilyAccessApiClient; }
    get catalogApiClient() { return useApiClientStore().catalogApiClient; }
    get organizationApiClient() { return useApiClientStore().organizationApiClient; }

    get getDisplayName() { return useAuthenticationStore().displayName; }
    canCreateSnapshotInOrganization = useAuthorizationStore().canCreateSnapshotInOrganization;

    // TODO: Change to composition api
    error = storeToRefs(useErrorStore()).errorMessage;
    // END TODO

    catalogFamilyAccess :ICatalogFamilyAccess = <ICatalogFamilyAccess> {};
    isLoading: boolean = false;
    retryableError: boolean = true;

    showOrganizationSelect: boolean = false;
    selectableOrganizations: IOrganization[];
    selectedOrganizationId: string = "";

    ACCEPT_COPY_SHARE_MAX_ATTEMPTS = 10;

    get catalogFamilyAccessId() {
        return this.$route.params.cfaid;
    }

    async created() {
        try 
        {
            const access = await this.catalogFamilyAccessApiClient.getOne(this.catalogFamilyAccessId);
            this.catalogFamilyAccess = access;

            // Already accepted
            if(this.catalogFamilyAccess.accessStartedTimestamp)
            {
                throw new Error();
            }
        } catch (e) {
            e.message = <string>this.$root.$t('COPIED_ENVIRONMENT_SHARE_NOT_VALID_MESSAGE')
            this.retryableError = false;
            useErrorStore().setError(e);
        }
    }

    async acceptAndNavigate(organizationId: string)
    {
        const createdEntryId = await this.catalogFamilyAccessApiClient.accept(this.catalogFamilyAccessId, organizationId);
        let familyId: string;
        if (this.catalogFamilyAccess.accessType === CatalogFamilyAccessType.Copied)
        {
            // The new family will be created asynchronously. Poll for family.
            let newFamilyId;
            let attempts = 0;
            while(!newFamilyId && attempts <= this.ACCEPT_COPY_SHARE_MAX_ATTEMPTS)
            {
                await new Promise((resolve) =>setTimeout(resolve, 1000));
                try
                {
                    const catalog = await this.catalogApiClient.getOne(createdEntryId);
                    if (catalog.familyIds.length)
                    {
                        newFamilyId = catalog.familyIds[0];
                    }
                }
                catch{}
                attempts++;
            }
            // If for some reason we can't find the family after running out of attempts, we'll still navigate to my-copied-environments.
            familyId = newFamilyId;
        }
        else
        {
            familyId = this.catalogFamilyAccess.catalogFamilyId
        }
        let ssoToken = await this.getSsoToken();
        window.location.href = new URL(`/my-copied-environments/${familyId}?s=${ssoToken}`, Config.CYBER_RANGE_UI_BASE_URL).href
    }

    async onLoginSuccess()
    {
        try 
        {
            this.isLoading = true;

            if (this.catalogFamilyAccess.accessType === CatalogFamilyAccessType.Linked)
            {
                await this.acceptAndNavigate(this.catalogFamilyAccess.organizationId);
                return;
            }
            else if (this.catalogFamilyAccess.accessType === CatalogFamilyAccessType.Copied)
            {
                await this.fetchClaims();
                const listableOrgs = await this.organizationApiClient.getAll();

                this.selectableOrganizations = listableOrgs.filter(o => !!this.canCreateSnapshotInOrganization(o.id));

                if (this.selectableOrganizations.length === 0)
                {
                    throw new ForbiddenApiClientError('');
                }
                else if (this.selectableOrganizations.length === 1)
                {
                    await this.acceptAndNavigate(this.selectableOrganizations[0].id);
                }
                else
                {
                    // Show picker
                    this.selectedOrganizationId = this.selectableOrganizations[0].id;
                    this.showOrganizationSelect = true;
                    this.isLoading = false; 
                }
            }
        } 
        catch(e)
        {
            if (e.statusCode === 403)
            {
                e.message = <string>this.$root.$t('COPIED_ENVIRONMENT_ACCEPT_SHARE_NO_PERMS_MESSAGE')
            }

            this.isLoading = false; 
            useErrorStore().setError(e);
        }
    }

    async onOrganizationSelectConfirmed()
    {
        this.isLoading = true;
        try
        {
            await this.acceptAndNavigate(this.selectedOrganizationId);
        }
        catch(e)
        {
            this.isLoading = false; 
            useErrorStore().setError(e);
        }
    }

    onOrganizationSelectCancelled(){
        this.showOrganizationSelect = false;
    }
}
</script>
